/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller, { inject as controller } from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import SubjectReportVisibility from '@linc-technologies/linced-common/enums/subject-report-visibility';
import SubjectType from '@linc-technologies/linced-common/enums/subject-type';
import config from 'linced-app/config/environment';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

// BULK_DELETE_FAILED marks a bulk delete failure whose error was already
// shown, so the caller doesn't show a second one.
const BULK_DELETE_FAILED = new Error('Bulk delete failed');

// destroyOrRestore deletes a record, and on failure puts it back: undeleted,
// with any relationship edits reverted (such as a cleared categories list).
function destroyOrRestore(record) {
  return record.destroyRecord().catch((err) => {
    rollbackRecord(record);
    throw err;
  });
}

export default class SubjectController extends Controller {
  @controller('goals')
  goalsController;

  @action
  setGoalExemplar(value) {
    this.goalExemplar = value;
  }

  @action
  setGoalPeopleDescription(value) {
    this.goalPeopleDescription = value;
  }

  // Services
  @service
  features;

  @service
  fetch;

  @service
  options;

  @service
  paperToaster;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  Scopes = Scopes;
  SubjectReportVisibility = SubjectReportVisibility;
  queryParams = ['subject-currentTab'];

  @alias('features.sidekickEnabled')
  sidekickEnabled;

  @computed('session.scopes.[]')
  get isAdmin() {
    return this.permissions.meet('urn:linced:organisation:role:admin');
  }

  // State
  @tracked
  view = 'grid';

  // deleteGoalSubjectDialogOpen provides the dialog opened when
  // deleting a goal subject
  @tracked
  deleteGoalSubjectDialogOpen = false;

  // deleteGoalCategoryDialogOpen provides the dialog opened when
  // deleting a goal category
  @tracked
  deleteGoalCategoryDialogOpen = false;

  @tracked
  deletingCategory = null;

  // deleteGoalSubcategoryDialogOpen provides the dialog opened when
  // deleting a goal subcategory
  @tracked
  deleteGoalSubcategoryDialogOpen = false;

  @tracked
  deletingSubcategory = null;

  // statesDeleteDialogOpen provides the dialog opened state for deleting goal
  // states.
  @tracked
  statesDeleteDialogOpen = false;

  // statesDeleteInProgress provides a state to inform the delete dialog that
  // the deletion of goal states is being processed.
  @tracked
  statesDeleteInProgress = false;

  // statesLoading provides a state to inform the async button that goal states
  // are still being queried for.
  @tracked
  statesLoading = false;

  // statesNumToDelete provides the total count of selected goal states to be
  // delete.
  @tracked statesNumToDelete = 0;

  // statesNumDeleted provides a count of the number of goal states that have
  // currently been deleted.
  @tracked statesNumDeleted = 0;

  // statesToDelete contains the array of selected goal states to be deleted.
  @tracked
  statesToDelete = null;

  isPLP = false;
  @tracked
  categoryDialogOpen = false;
  @tracked
  editingCategory = null;
  @tracked
  subcategoryDialogOpen = false;
  @tracked
  editingSubcategory = null;
  @tracked
  goalDialogOpen = false;
  goalSaveInProgress = false;
  @tracked
  editingGoal = null;
  @tracked
  goalLabel = '';
  @tracked
  goalEducatorDescription = '';
  @tracked
  goalPeopleDescription = '';
  @tracked
  goalExemplar = '';

  // Success Criteria Language
  @tracked
  SC1 = '';

  @tracked
  SC2 = '';
  @tracked
  SC3 = '';
  @tracked
  SC4 = '';
  @tracked
  SC5 = '';

  // updateSuccessCriteria writes the per-grade success-criteria label. The grade
  // is curried in by `{{fn}}` so the input's value arrives last. `SC1`-`SC5` are
  // tracked, so the computed-key assignment goes through the tracked setter.
  @action
  updateSuccessCriteria(gradeValue, value) {
    this[`SC${gradeValue}`] = value;
  }
  @tracked
  entityToDelete = null;
  editorOptions = Object.freeze(defaultTinyMCEConfig());

  @tracked
  deleteDialogOpen;

  @tracked
  goalCategory;

  @tracked
  goalSubcategory;

  @tracked
  subcategoryParent;

  @tracked
  entityTypeToDelete;

  @computed('session.scopes.[]')
  get isHeroAdmin() {
    return this.permissions.meet(Scopes.LINCED_MANAGER);
  }

  @computed('model.subject.subjectType')
  get subjectType() {
    return findBy(SubjectType.values, 'value', this.model.subject.subjectType);
  }

  @computed(
    'model.subject.{visibleToTeachers,visibleToParents,reportVisibility}',
  )
  get showReportVisibility() {
    let { visibleToTeachers, visibleToParents, reportVisibility } =
      this.model.subject;
    return (
      (visibleToTeachers || visibleToParents) &&
      reportVisibility !== SubjectReportVisibility.NEVER
    );
  }

  @computed('subjectType.hasContent')
  get hasContent() {
    return isEmpty(this.subjectType) || this.subjectType.hasContent;
  }

  @computed('subjectType.schoolCanEdit', 'isHeroAdmin')
  get canEdit() {
    let userIsHeroAdmin = this.isHeroAdmin;
    // Check if the user is an admin or the subjectType allows school-level editing
    return userIsHeroAdmin || this.get('subjectType.schoolCanEdit');
  }

  @computed('canEdit')
  get readOnly() {
    // Set readOnly to be the inverse of canEdit
    return !this.canEdit;
  }

  @computed('model.pages.@each.{goalSubject_ids,label}', 'model.subject.id')
  get filteredPages() {
    let pages = this.get('model.pages');
    let subjectId = this.get('model.subject.id');

    return pages
      .filter((page) => page.goalSubject_ids.includes(subjectId))
      .map((page) => page.label);
  }

  // Methods
  // _getGoalStates returns all the current goal states for a given school
  // based on the provided params.
  _getGoalStates(schoolId, subjectId, categoryId, subcategoryId, goalId) {
    return this.store.query('goals/state', {
      schoolId,
      subjectId,
      categoryId,
      subcategoryId,
      goalId,
    });
  }

  // statesDeletedPercent calculates the percentage of states deleted to inform
  // a determinate loading component.
  get statesDeletedPercent() {
    if (this.statesNumToDelete > 0) {
      return (this.statesNumDeleted / this.statesNumToDelete) * 100;
    }
    return 0;
  }

  // deleteStates performs the deletion of the selected goal states.
  @action
  deleteStates() {
    this.statesDeleteInProgress = true;
    this.statesNumDeleted = 0;

    // perform state deletion sequentially using reduce.
    const states = this.statesToDelete;
    states
      .reduce((previousPromise, nextState) => {
        return previousPromise.then(() => {
          return nextState.destroyRecord().then(() => {
            this.incrementProperty('statesNumDeleted');
            return Promise.resolve();
          });
        });
      }, Promise.resolve())
      .then(() => {
        this.statesDeleteDialogOpen = false;
        this.statesDeleteInProgress = false;

        this.paperToaster.show(`Successfully deleted Goal states.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
      })
      .catch(() => {
        this.paperToaster.show(
          `Error deleting a Goal State. Please try again.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      });
  }

  // getStatesToDelete performs a query to get the current states and opens
  // the delete state dialog.
  @action
  getStatesToDelete(type, entity) {
    this.statesLoading = true;

    let states;
    switch (type) {
      case 'subject':
        states = this._getGoalStates(entity.get('school.id'), entity.id);
        break;

      case 'category':
        states = this._getGoalStates(
          entity.get('school.id'),
          entity.subject_id,
          entity.id,
        );
        break;

      case 'subcategory':
        states = this._getGoalStates(
          entity.get('school.id'),
          entity.subject_id,
          entity.category_id,
          entity.id,
        );
        break;

      case 'goal':
        states = this._getGoalStates(
          entity.get('school.id'),
          entity.subject_id,
          entity.category_id,
          entity.subcategory_id,
          entity.id,
        );
        break;

      default:
        this.paperToaster.show(
          `Entity provided to delete goal states is not supported.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
        return;
    }

    states
      .then((states) => {
        // Close currently open edit dialogs...
        this.categoryDialogOpen = false;
        this.goalDialogOpen = false;
        this.subcategoryDialogOpen = false;
        this.statesLoading = false;

        if (isEmpty(states)) {
          this.paperToaster.show(`No Goal States to delete!`, {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
          return;
        }

        this.statesDeleteDialogOpen = true;
        this.statesNumToDelete = states.length;
        this.statesToDelete = states;
      })
      .catch(() => {
        this.statesLoading = false;
        this.paperToaster.show(`Error getting Goal States, please try again.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      });
  }

  @action
  edit() {
    this.goalsController.editSubject(this.get('model.subject'));
  }

  @action
  addCategory() {
    this.editingCategory = null;
    this.categoryDialogOpen = true;
  }

  @action
  editCategory(category) {
    this.editingCategory = category;
    this.categoryDialogOpen = true;
  }

  @action
  closeCategoryDialog() {
    this.categoryDialogOpen = false;
  }

  @action
  categorySaved() {
    this.get('model.categories').update();
  }

  @action
  addSubcategory(category) {
    this.subcategoryParent = category;
    this.editingSubcategory = null;
    this.subcategoryDialogOpen = true;
  }

  @action
  editSubcategory(subcategory) {
    this.subcategoryParent = null;
    this.editingSubcategory = subcategory;
    this.subcategoryDialogOpen = true;
  }

  @action
  closeSubcategoryDialog() {
    this.subcategoryDialogOpen = false;
  }

  @action
  subcategorySaved() {
    this.get('model.subcategories').update();
  }

  // setGoalDescription sets the label to be the educators description in
  // order to reduce typing/copy+paste.
  @action
  setGoalDescription(description) {
    this.goalLabel = description;
    this.goalEducatorDescription = description;
  }

  @action
  addGoal(category, subcategory) {
    this.resetGoal();
    this.goalCategory = category;
    this.goalSubcategory = subcategory;
    this.goalDialogOpen = true;
  }

  @action
  editGoal(goal) {
    this.resetGoal();
    this.editingGoal = goal;
    this.goalLabel = goal.get('label');
    this.goalEducatorDescription = goal.get('educatorDescription');
    this.goalPeopleDescription = goal.get('peopleDescription');
    this.goalExemplar = goal.get('exemplar');
    this.SC1 = goal.get('SC1');
    this.SC2 = goal.get('SC2');
    this.SC3 = goal.get('SC3');
    this.SC4 = goal.get('SC4');
    this.SC5 = goal.get('SC5');

    this.goalDialogOpen = true;
  }

  @action
  closeGoalDialog() {
    this.goalDialogOpen = false;
  }

  @action
  saveGoal() {
    let goal = this.editingGoal;
    let toaster = this.paperToaster;

    if (isEmpty(goal)) {
      let category = this.goalCategory;
      let subcategory = this.goalSubcategory;
      let sequence =
        (isEmpty(subcategory)
          ? category.get('goals.length')
          : subcategory.get('goals.length')) + 1;

      goal = this.store.createRecord('goals/goal', {
        schoolId: this.get('session.school'),
        authorId: this.get('session.person'),
        subjectId: this.get('model.subject'),
        categoryId: category,
        subcategoryId: subcategory,
        sequence,
      });
    }

    goal.set('label', this.goalLabel);
    goal.set('educatorDescription', this.goalEducatorDescription);
    goal.set('peopleDescription', this.goalPeopleDescription);
    goal.set('exemplar', this.goalExemplar);

    goal.set('SC1', this.SC1);
    goal.set('SC2', this.SC2);
    goal.set('SC3', this.SC3);
    goal.set('SC4', this.SC4);
    goal.set('SC5', this.SC5);

    goal
      .save()
      .then(() => {
        this.goalDialogOpen = false;
        toaster.show(`Goal "${goal.get('label')}" saved.`, {
          duration: 4000,
          position: 'top right',
        });
      })
      .catch(() => {
        // Unload a new record and revert an edited one; the form keeps the input.
        rollbackRecord(goal);
        toaster.show(`Error saving goal.`, {
          duration: 4000,
          position: 'top right',
        });
      })
      .finally(() => {
        this.get('model.goals').update();
      });
  }

  // bulkDelete posts to one of the goals API's :bulkdelete endpoints. On
  // failure it shows the error and rejects with BULK_DELETE_FAILED, so the
  // caller stops before deleting the parent record.
  bulkDelete(collection, data) {
    let uri = `${config.APP.apiHost}/goals/${config.APP.apiPath}/${collection}/:bulkdelete`;
    return this.fetch.post(uri, { data }).catch(() => {
      this.toaster.error(
        `An error occurred while deleting ${collection}. Please try again.`,
      );
      throw BULK_DELETE_FAILED;
    });
  }

  @action
  deleteGoalCategory(category) {
    let promises = [];
    let data = {
      schoolId: category.get('school.id'),
      subjectId: category.subject_id,
      categoryId: category.id,
    };
    if (category.goals.length) {
      promises.push(this.bulkDelete('goals', data));
    }
    if (category.subcategories.length) {
      promises.push(this.bulkDelete('subcategories', data));
    }

    return Promise.all(promises)
      .then(() => destroyOrRestore(category))
      .then(() => {
        this.deleteGoalCategoryDialogOpen = false;
        this.toaster.success('Goal Category has been deleted successfully.');
      })
      .catch((err) => {
        if (err !== BULK_DELETE_FAILED) {
          this.toaster.error(
            'An error occurred while deleting Subject items. Please try again.',
          );
        }
      });
  }

  @action
  deleteGoalSubcategory(subcategory) {
    let promises = [];
    if (subcategory.goals.length) {
      subcategory.goals.forEach((goal) => {
        promises.push(destroyOrRestore(goal));
      });
    }

    return Promise.all(promises)
      .then(() => destroyOrRestore(subcategory))
      .then(() => {
        this.deleteGoalSubcategoryDialogOpen = false;
        this.toaster.success('Goal Subcategory has been deleted successfully.');
      })
      .catch(() => {
        this.toaster.error(
          'An error occurred while deleting the subcategory. Please try again.',
        );
      });
  }

  @action
  deleteGoalSubjectItems(entity) {
    let data = {
      schoolId: entity.get('school.id'),
      subjectId: entity.id,
    };
    let promises = [];
    if (this.model.goals.length) {
      promises.push(this.bulkDelete('goals', data));
    }
    if (this.model.categories.length) {
      promises.push(this.bulkDelete('categories', data));
      if (this.model.subcategories.length) {
        promises.push(this.bulkDelete('subcategories', data));
      }
    }

    // Only clear the subject's categories and delete it once every child
    // was deleted; a failed bulk delete used to be swallowed here.
    return Promise.all(promises)
      .then(() => {
        this.set('model.subject.categories', []);
        this.deleteGoalSubjectDialogOpen = false;
        return destroyOrRestore(entity);
      })
      .then(() => {
        // The entire subject was deleted; navigate back to the Goals admin page
        this.router.transitionTo('goals');
        this.toaster.success('Goal Subject has been deleted successfully.');
      })
      .catch((err) => {
        if (err !== BULK_DELETE_FAILED) {
          this.toaster.error(
            'An error occurred while deleting Goal Subject. Please try again.',
          );
        }
      });
  }

  @action
  openDeleteSubjectDialog(entity) {
    this._getGoalStates(entity.get('school.id'), entity.id).then((states) => {
      this.statesToDelete = states;
      this.deleteGoalSubjectDialogOpen = true;
    });
  }

  @action
  openDeleteCategoryDialog(entity) {
    this._getGoalStates(
      entity.get('school.id'),
      entity.subject_id,
      entity.id,
    ).then((states) => {
      this.statesToDelete = states;
      this.deletingCategory = entity;
      this.deleteGoalCategoryDialogOpen = true;
    });
  }

  @action
  openDeleteSubcategoryDialog(entity) {
    this._getGoalStates(
      entity.get('school.id'),
      entity.subject_id,
      entity.category_id,
      entity.id,
    ).then((states) => {
      this.statesToDelete = states;
      this.deletingSubcategory = entity;
      this.deleteGoalSubcategoryDialogOpen = true;
    });
  }

  @action
  confirmDeleteEntity(type, entity) {
    this.entityTypeToDelete = type;
    this.entityToDelete = entity;
    this.deleteDialogOpen = true;
  }

  @action
  closeDeleteDialog() {
    this.deleteDialogOpen = false;
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    const moved = sourceList !== targetList;
    if (moved) {
      // Moved to a new parent category/subcategory - update the relationship.
      // A category's own list has no subcategory.
      item.setProperties({
        categoryId: targetList.__category__,
        subcategoryId: targetList.__subcategory__ ?? null,
      });
    }

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
      let saved = entity.save();
      if (moved && entity === item) {
        // Put it back under its old category/subcategory; the lists are
        // built from the relationships, so it moves back on screen too.
        saved.catch(() => {
          rollbackRecord(entity);
          this.toaster.error(`Error moving ${entity.get('label')}`);
        });
      }
    });
  }

  @action
  setView(newState) {
    this.view = newState;
  }

  resetGoal() {
    this.editingGoal = null;
    this.goalLabel = '';
    this.goalEducatorDescription = '';
    this.goalPeopleDescription = '';
    this.goalExemplar = '';
    this.SC1 = '';
    this.SC2 = '';
    this.SC3 = '';
    this.SC4 = '';
    this.SC5 = '';
  }
}
